import { BRAND_CONFIG } from '@@brand'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { useFetchLoyaltyLevelInfo } from '@entities/Loyalty'

import { useModalStore } from '@features/modal'

import { formatPrice } from '@shared/uttils'
import { formatDayMonth } from '@shared/uttils/formatDayMonth'

import { ModalLoyaltyLevels } from './ModalLoyaltyLevels'

import styles from './styles.module.scss'

interface ICurrentLevelInfoProps {
  className?: string
}

export const CurrentLevelInfo: React.FC<ICurrentLevelInfoProps> = ({ className }) => {
  const { t: tn } = useTranslation('personal')
  const { openModal } = useModalStore()
  const { loyaltyStatusInfo, loading } = useFetchLoyaltyLevelInfo()

  // Если нет данных или нет текущего уровня - не показываем компонент
  if (loading || !loyaltyStatusInfo?.currentLevel) {
    return null
  }

  const { currentLevel, nextLevel, currentValue } = loyaltyStatusInfo

  // Вычисляем прогресс до следующего уровня
  const progress = nextLevel?.needToNextLevel && currentValue > -1
    ? Math.min(currentValue / (currentValue + nextLevel.needToNextLevel), 1)
    : 0

  const handleOpenLevels = () => {
    if (!nextLevel) return
    openModal('loyalty-levels', <ModalLoyaltyLevels />, {
      title: tn('bonus.loyaltyLevels'),
    })
  }

  const isClickable = !!nextLevel

  return (
    <div 
      className={classNames(styles.levelInfo, className, {
        [styles['levelInfo--clickable']]: isClickable,
      })}
      onClick={isClickable ? handleOpenLevels : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && handleOpenLevels() : undefined}
    >
      <div className={styles.levelInfo__header}>
        { nextLevel && currentValue > -1 && (
          <div className={styles.levelInfo__current}>
            <span className={styles.levelInfo__label}>{tn('bonus.currentStatus')}</span>
            <span className={styles.levelInfo__name}>{currentLevel.name}</span>
          </div>
        )}
        <div className={styles.levelInfo__cashback}>
          <span className={styles.levelInfo__label}>{tn('bonus.cashback')}</span>
          <span className={styles.levelInfo__value}>
            {currentLevel.preference}{currentLevel.preferencePostfix ?? '%'}
          </span>
        </div>
      </div>

      {nextLevel && currentValue > -1 ? (
        <div className={styles.levelInfo__progress}>
          <div className={styles.levelInfo__progressInfo}>
            <span className={styles.levelInfo__toNext}>
              {formatPrice(nextLevel.needToNextLevel, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
                style: 'decimal',
              })} {BRAND_CONFIG.currency.symbol}
            </span>
            <span className={styles.levelInfo__nextText}>
              {tn('bonus.toNextLevel', {
                preference: `${nextLevel.preference}${nextLevel.preferencePostfix}`,
                date: formatDayMonth(new Date(nextLevel.nextCalcDate))
              })}
            </span>
          </div>
          <div className={styles.levelInfo__progressBar}>
            <div 
              className={styles.levelInfo__progressFill}
              style={{ width: `${Math.max(progress * 100, 2)}%` }}
            />
          </div>
        </div>
      ) : currentLevel.description ? (
        <div className={styles.levelInfo__description}>
          { currentLevel.description.split('•').map((l, index) => (
            <p key={index}>{ index > 0 ? '•' : '' }{ l }</p>
          ))}
        </div>
      ) : null}
    </div>
  )
}
